import { lazy, Suspense } from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { Seo } from './components/Seo.jsx';

// Pages
import Landing from './pages/Landing.jsx';
import SuiteLauncher from './pages/SuiteLauncher.jsx';
import Legal from './pages/Legal.jsx';
// V65 — games lazy-loaded so they don't ship in the main entry chunk.
// Each becomes its own Rollup chunk, fetched on navigation. The
// top-level <Suspense> boundary in this file already covers them.
const PeriodicQuest   = lazy(() => import('./games/PeriodicQuest.jsx'));
const QuarkForge      = lazy(() => import('./games/quarkforge/QuarkForge.jsx'));
const CodonQuest      = lazy(() => import('./games/CodonQuest.jsx'));
const AnatomyPark     = lazy(() => import('./games/AnatomyPark.jsx'));
const UnitCircleQuest = lazy(() => import('./games/UnitCircleQuest.jsx'));
const MathicQuest     = lazy(() => import('./games/MathicQuest.jsx'));

// V63.1 — first NerdHistory game
const RomanProvince = lazy(() => import('./games/romanprovince/RomanProvince.jsx'));

// V63.2 — first NerdMusic game
const FilmScoring = lazy(() => import('./games/filmscoring/FilmScoring.jsx'));

// V63.3 — first orbital-mechanics game. Lazy-loaded because the engine
// and mission catalog together are ~50KB; no need to pay it on the
// landing page.
const MissionControl = lazy(() => import('./games/missioncontrol/MissionControl.jsx'));

// V44 — 10 new math games (numberline-drop through constructor)
const NumberLineDrop  = lazy(() => import('./games/numberline/NumberLineDrop.jsx'));
const SlopeSlider     = lazy(() => import('./games/slopeslider/SlopeSlider.jsx'));
const Trajectory      = lazy(() => import('./games/trajectory/Trajectory.jsx'));
const Funcion         = lazy(() => import('./games/funcion/Funcion.jsx'));
const AreaArchitect   = lazy(() => import('./games/areaarchitect/AreaArchitect.jsx'));
const TransformTetris = lazy(() => import('./games/transformtetris/TransformTetris.jsx'));
const Estimate        = lazy(() => import('./games/estimate/Estimate.jsx'));
const TriangleForge   = lazy(() => import('./games/triangleforge/TriangleForge.jsx'));
const Modular         = lazy(() => import('./games/modular/Modular.jsx'));
const Constructor     = lazy(() => import('./games/constructor/Constructor.jsx'));

// V44.2 — 5 new bio games (Bio Claude's contribution, integrated this pass)
const Pedigree        = lazy(() => import('./games/pedigree/Pedigree.jsx'));
const PhaseLab        = lazy(() => import('./games/phaselab/PhaseLab.jsx'));
const Selection       = lazy(() => import('./games/selection/Selection.jsx'));
const CodonReader     = lazy(() => import('./games/codonreader/CodonReader.jsx'));
const Membrane        = lazy(() => import('./games/membrane/Membrane.jsx'));

// V44 — 4 family home pages (subgrids per game family)
import MathFamilyHome from './games/_family/MathHome.jsx';
import ChemFamilyHome from './games/_family/ChemHome.jsx';
import BioFamilyHome from './games/_family/BioHome.jsx';
import PhysFamilyHome from './games/_family/PhysHome.jsx';
import HistoryFamilyHome from './games/_family/HistoryHome.jsx';
// V63.2 — music family home (REQUIRED — prevents /games/music 404)
import MusicFamilyHome from './games/_family/MusicHome.jsx';

// V44 — SEO wrapper + per-game SEO content (Pass 3-4)
import { GameWithSeo } from './games/GameWithSeo.jsx';
import NumberLineDropSeo from './games/seo/NumberLineDropSeo.jsx';
import SlopeSliderSeo from './games/seo/SlopeSliderSeo.jsx';
import TrajectorySeo from './games/seo/TrajectorySeo.jsx';
import FuncionSeo from './games/seo/FuncionSeo.jsx';
import AreaArchitectSeo from './games/seo/AreaArchitectSeo.jsx';
import TransformTetrisSeo from './games/seo/TransformTetrisSeo.jsx';
import EstimateSeo from './games/seo/EstimateSeo.jsx';
import TriangleForgeSeo from './games/seo/TriangleForgeSeo.jsx';
import ModularSeo from './games/seo/ModularSeo.jsx';
import ConstructorSeo from './games/seo/ConstructorSeo.jsx';

// V45 — 2 new chem games + their SEO
const ChemForge       = lazy(() => import('./games/chemforge/ChemForge.jsx'));
const MoleForge       = lazy(() => import('./games/moleforge/MoleForge.jsx'));
import ChemForgeSeo from './games/seo/ChemForgeSeo.jsx';
import MoleForgeSeo from './games/seo/MoleForgeSeo.jsx';

import ComingSoon from './pages/ComingSoon.jsx';
import Suggest from './pages/Suggest.jsx';

// Tool components
import GradeCalc from './tools/GradeCalc.jsx';
import StudyTimer from './tools/StudyTimer.jsx';
import WordCount from './tools/WordCount.jsx';
import PercentCalc from './tools/PercentCalc.jsx';
import UnitConvert from './tools/UnitConvert.jsx';
import AverageCalc from './tools/AverageCalc.jsx';

// NerdChem sub-suite (Phase 3). Added as /chem/* routes alongside the
// existing root NerdStack — the root→/stack/ migration is deferred to its
// own deliberate session per the risk-isolation principle.
import ChemHome from "./chem/ChemHome.jsx";
import PeriodicTable from './chem/PeriodicTable.jsx';
import MolecularWeight from './chem/MolecularWeight.jsx';
import MoleCalculator from './chem/MoleCalculator.jsx';
import PhCalculator from './chem/PhCalculator.jsx';
import Dilution from './chem/Dilution.jsx';
import SigFigs from './chem/SigFigs.jsx';
import EquationBalancer from './chem/EquationBalancer.jsx';
import GasLaws from './chem/GasLaws.jsx';
import Stoichiometry from './chem/Stoichiometry.jsx';
// V51 — reference pages are lazy-loaded so each one and its data ship
// as a separate chunk. This keeps the initial bundle small even with
// 5 references totaling ~1,600 items.
const ChemReference = lazy(() => import('./chem/ChemReference.jsx'));

// NerdMath sub-suite (lean set). Added as /math/* alongside root, same
// Option-B pattern as /chem — NOT the deferred root→/stack/ migration.
import { MathLanding, MathHome } from './math/MathHome.jsx';
import { GamesLanding, GamesHome } from './games/GamesHome.jsx';
import MathCalculator from './math/Calculator.jsx';
import MathQuadratic from './math/Quadratic.jsx';
import MathTrig from './math/Trig.jsx';
import MathLogarithm from './math/Logarithm.jsx';
import MathMatrix from './math/Matrix.jsx';

// NerdPhys sub-suite — staged get-ahead build (its committed slot is
// after Phase 2 paid AI). Same Option-B pattern as /chem and /math.
import { PhysLanding, PhysHome } from './phys/PhysHome.jsx';
import PhysProjectile from './phys/Projectile.jsx';
import PhysKinematics from './phys/Kinematics.jsx';
import PhysForce from './phys/Force.jsx';
import PhysEnergy from './phys/Energy.jsx';
import PhysOhms from './phys/Ohms.jsx';
import PhysParticles from './phys/Particles.jsx';
import PhysWaves from './phys/Waves.jsx';
import PhysMomentum from './phys/Momentum.jsx';
const PhysReference = lazy(() => import('./phys/PhysReference.jsx'));

// NerdTools sub-suite — device-utility only (StudyTimer/WordCount stay
// in NerdStack; that resolves the old overlap-boundary question). Same
// Option-B pattern as the other suites.
import { ToolsLanding, ToolsHome } from './tools/ToolsHome.jsx';
import ToolsConverter from './tools/Converter.jsx';
// V53 — NerdTools expansion. Level Pro REPLACES Level at the same path.
import ToolsLevelPro from './tools/LevelPro.jsx';
// Family 1 — Level & Measure
import ToolsPictureHanging from './tools/PictureHangingGuide.jsx';
import ToolsObjectHeight from './tools/ObjectHeight.jsx';
import ToolsSymmetry from './tools/SymmetryChecker.jsx';
import ToolsPhotoStraightener from './tools/PhotoStraightener.jsx';
// Family 2 — Visual & Camera
import ToolsMagnifier from './tools/Magnifier.jsx';
import ToolsColorPicker from './tools/ColorPicker.jsx';
import ToolsBrightness from './tools/BrightnessMeter.jsx';
import ToolsGridCamera from './tools/GridOverlayCamera.jsx';
import ToolsBeforeAfter from './tools/BeforeAfter.jsx';
// Family 3 — Sound & Audio
import ToolsTone from './tools/ToneGenerator.jsx';
import ToolsTuningFork from './tools/TuningFork.jsx';
import ToolsMetronome from './tools/Metronome.jsx';
import ToolsSpectrogram from './tools/Spectrogram.jsx';
// Family 4 — Outdoor & Navigation
import ToolsGPS from './tools/GPSCoordinates.jsx';
import ToolsAltimeter from './tools/Altimeter.jsx';
import ToolsSunMoon from './tools/SunMoonDirection.jsx';
import ToolsTrailMarker from './tools/TrailMarker.jsx';
// Family 5 — DIY & Workshop
import ToolsMetalDetector from './tools/MetalDetector.jsx';
import ToolsSquareChecker from './tools/SquareChecker.jsx';
import ToolsDrillBit from './tools/DrillBitGuide.jsx';
import ToolsRoofPitch from './tools/RoofPitch.jsx';
import ToolsCoverage from './tools/CoverageCalc.jsx';
// Family 6 — Math & Engineering
import ToolsFraction from './tools/FractionCalc.jsx';
import ToolsRightTriangle from './tools/RightTriangle.jsx';
import ToolsResistor from './tools/ResistorColorCode.jsx';
import ToolsTorque from './tools/BoltTorque.jsx';
// Family 7 — Time & Counting
import ToolsInterval from './tools/IntervalTimer.jsx';
import ToolsTally from './tools/TallyCounter.jsx';
import ToolsReaction from './tools/ReactionTimer.jsx';
// Family 8 — Sensor Lab
import ToolsVibration from './tools/VibrationMeter.jsx';
import ToolsMagneticField from './tools/MagneticFieldVisualizer.jsx';
import ToolsSteadiness from './tools/HandSteadiness.jsx';
import ToolsShakeCounter from './tools/ShakeCounter.jsx';
// Family 9 — Practical / Generators
import ToolsCurrency from './tools/CurrencyConverter.jsx';
import ToolsPassword from './tools/PasswordGenerator.jsx';
import ToolsExif from './tools/ExifRemover.jsx';
import ToolsCompass from './tools/Compass.jsx';
import ToolsDecibel from './tools/Decibel.jsx';
import ToolsRuler from './tools/Ruler.jsx';
import ToolsQR from './tools/QR.jsx';

// NerdBio sub-suite — genetics/molecular tools + anatomy reference.
// Honestly scoped (smallest market, no medical/clinical calc by
// deliberate decision). Same Option-B pattern as the other suites.
import { BioLanding, BioHome } from './bio/BioHome.jsx';
import BioDNA from './bio/DNA.jsx';
import BioTranslate from './bio/Translate.jsx';
import BioPunnett from './bio/Punnett.jsx';
import BioHardyWeinberg from './bio/HardyWeinberg.jsx';
import BioLab from './bio/Lab.jsx';
const BioAnatomy = lazy(() => import('./bio/Anatomy.jsx'));
const BioReference = lazy(() => import('./bio/BioReference.jsx'));
const MathReference = lazy(() => import('./math/MathReference.jsx'));
const MusicReference = lazy(() => import('./music/MusicReference.jsx'));
const HistoryReference = lazy(() => import('./history/HistoryReference.jsx'));

// V62.1 — NerdMusic interactive tools (Circle of Fifths, Scale Builder).
const CircleOfFifths = lazy(() => import('./music/CircleOfFifths.jsx'));
const ScaleBuilder = lazy(() => import('./music/ScaleBuilder.jsx'));

// V52 — NerdMusic family home (eager — small landing page)
import MusicHome from './music/MusicHome.jsx';

// V55 — NerdHistory family home (eager — small landing page)
import HistoryHome from './history/HistoryHome.jsx';

// 404 — when someone hits a URL that doesn't match anything.
function NotFound() {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0A0B14',
      color: '#E2E8F0',
      fontFamily: "'DM Sans', system-ui, sans-serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      textAlign: 'center',
    }}>
      <h1 style={{ fontSize: 64, fontWeight: 800, margin: 0 }}>404</h1>
      <p style={{ fontSize: 17, color: '#94A3B8', margin: '12px 0 32px' }}>
        That tool doesn't exist. Yet.
      </p>
      <Link
        to="/"
        style={{
          padding: '12px 28px',
          background: '#8B5CF6',
          color: 'white',
          borderRadius: 12,
          textDecoration: 'none',
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        ← Back to NerdStack
      </Link>
    </div>
  );
}

export default function App() {
  return (
    <>
      {/* Seo lives at the top of App and reads location internally. */}
      {/* Each route's metadata comes from src/lib/seo.js */}
      <Seo />
      <Suspense fallback={<div style={{ minHeight: "100vh", background: "#0A0B14" }} />}>
      <Routes>
      {/* Landing — 6-suite launcher (NerdStack is a peer suite) */}
      <Route path="/" element={<SuiteLauncher />} />
      <Route path="/legal" element={<Legal />} />
      {/* NerdGames suite — family-organized as of V44 */}
      <Route path="/games" element={<GamesHome />} />
      <Route path="/games/home" element={<GamesHome />} />

      {/* Family subgrid pages */}
      <Route path="/games/math" element={<MathFamilyHome />} />
      <Route path="/games/chem" element={<ChemFamilyHome />} />
      <Route path="/games/bio" element={<BioFamilyHome />} />
      <Route path="/games/phys" element={<PhysFamilyHome />} />
      <Route path="/games/history" element={<HistoryFamilyHome />} />
      <Route path="/games/music" element={<MusicFamilyHome />} />

      {/* Math games — 12 total (10 new + 2 migrated) */}
      <Route path="/games/math/menage-a-trig" element={<UnitCircleQuest />} />
      <Route path="/games/math/mathic-quest" element={<MathicQuest />} />
      <Route path="/games/math/numberline-drop" element={<GameWithSeo Game={NumberLineDrop} Seo={NumberLineDropSeo} />} />
      <Route path="/games/math/slope-slider" element={<GameWithSeo Game={SlopeSlider} Seo={SlopeSliderSeo} />} />
      <Route path="/games/math/trajectory" element={<GameWithSeo Game={Trajectory} Seo={TrajectorySeo} />} />
      <Route path="/games/math/funcion" element={<GameWithSeo Game={Funcion} Seo={FuncionSeo} />} />
      <Route path="/games/math/area-architect" element={<GameWithSeo Game={AreaArchitect} Seo={AreaArchitectSeo} />} />
      <Route path="/games/math/transform-tetris" element={<GameWithSeo Game={TransformTetris} Seo={TransformTetrisSeo} />} />
      <Route path="/games/math/estimate" element={<GameWithSeo Game={Estimate} Seo={EstimateSeo} />} />
      <Route path="/games/math/triangle-forge" element={<GameWithSeo Game={TriangleForge} Seo={TriangleForgeSeo} />} />
      <Route path="/games/math/modular" element={<GameWithSeo Game={Modular} Seo={ModularSeo} />} />
      <Route path="/games/math/constructor" element={<GameWithSeo Game={Constructor} Seo={ConstructorSeo} />} />

      {/* Chem games */}
      <Route path="/games/chem/periodic-quest" element={<PeriodicQuest />} />
      <Route path="/games/chem/chemforge" element={<GameWithSeo Game={ChemForge} Seo={ChemForgeSeo} />} />
      <Route path="/games/chem/moleforge" element={<GameWithSeo Game={MoleForge} Seo={MoleForgeSeo} />} />

      {/* Bio games */}
      <Route path="/games/bio/gene-pool" element={<CodonQuest />} />
      <Route path="/games/bio/anatomy-park" element={<AnatomyPark />} />
      <Route path="/games/bio/pedigree" element={<Pedigree />} />
      <Route path="/games/bio/phase-lab" element={<PhaseLab />} />
      <Route path="/games/bio/selection" element={<Selection />} />
      <Route path="/games/bio/codon-reader" element={<CodonReader />} />
      <Route path="/games/bio/membrane" element={<Membrane />} />

      {/* Phys games */}
      <Route path="/games/phys/quarkforge" element={<QuarkForge />} />
      {/* V63.3 */}
      <Route path="/games/phys/mission-control" element={<MissionControl />} />

      {/* History games — V63.1 */}
      <Route path="/games/history/roman-province" element={<RomanProvince />} />

      {/* Music games — V63.2 */}
      <Route path="/games/music/film-scoring" element={<FilmScoring />} />

      {/* V44 — redirects from old flat /games/<name> URLs to new family-organized URLs.
          Preserves SEO from any existing indexing on the old paths. */}
      <Route path="/games/periodic-quest" element={<Navigate to="/games/chem/periodic-quest" replace />} />
      <Route path="/games/gene-pool" element={<Navigate to="/games/bio/gene-pool" replace />} />
      <Route path="/games/anatomy-park" element={<Navigate to="/games/bio/anatomy-park" replace />} />
      <Route path="/games/menage-a-trig" element={<Navigate to="/games/math/menage-a-trig" replace />} />
      <Route path="/games/mathic-quest" element={<Navigate to="/games/math/mathic-quest" replace />} />
      <Route path="/games/quarkforge" element={<Navigate to="/games/phys/quarkforge" replace />} />

      {/* NerdStack suite — the 9 root tools, now under /stack */}
      <Route path="/stack" element={<Landing />} />
      <Route path="/stack/gradecalc" element={<GradeCalc />} />
      <Route path="/stack/timer" element={<StudyTimer />} />
      <Route path="/stack/wordcount" element={<WordCount />} />
      <Route path="/stack/percent" element={<PercentCalc />} />
      <Route path="/stack/convert" element={<UnitConvert />} />
      <Route path="/stack/average" element={<AverageCalc />} />
      <Route path="/stack/quizcard" element={<ComingSoon slug="quizcard" />} />
      <Route path="/stack/clearlens" element={<ComingSoon slug="clearlens" />} />

      {/* Old root-tool paths → redirect so existing deep links/bookmarks survive */}
      <Route path="/gradecalc" element={<Navigate to="/stack/gradecalc" replace />} />
      <Route path="/timer" element={<Navigate to="/stack/timer" replace />} />
      <Route path="/wordcount" element={<Navigate to="/stack/wordcount" replace />} />
      <Route path="/percent" element={<Navigate to="/stack/percent" replace />} />
      <Route path="/convert" element={<Navigate to="/stack/convert" replace />} />
      <Route path="/average" element={<Navigate to="/stack/average" replace />} />
      <Route path="/quizcard" element={<Navigate to="/stack/quizcard" replace />} />
      <Route path="/clearlens" element={<Navigate to="/stack/clearlens" replace />} />

      {/* NerdChem sub-suite — periodic table live, rest coming */}
      <Route path="/chem" element={<ChemHome />} />
      <Route path="/chem/periodic-table" element={<PeriodicTable />} />
      <Route path="/chem/molecular-weight" element={<MolecularWeight />} />
      <Route path="/chem/mole-calculator" element={<MoleCalculator />} />
      <Route path="/chem/ph-calculator" element={<PhCalculator />} />
      <Route path="/chem/dilution" element={<Dilution />} />
      <Route path="/chem/sig-figs" element={<SigFigs />} />
      <Route path="/chem/equation-balancer" element={<EquationBalancer />} />
      <Route path="/chem/gas-laws" element={<GasLaws />} />
      <Route path="/chem/stoichiometry" element={<Stoichiometry />} />
      <Route path="/chem/reference" element={<ChemReference />} />

      {/* NerdMath sub-suite — lean set */}
      <Route path="/math" element={<MathHome />} />
      <Route path="/math/home" element={<MathHome />} />
      <Route path="/math/calculator" element={<MathCalculator />} />
      <Route path="/math/quadratic" element={<MathQuadratic />} />
      <Route path="/math/trig" element={<MathTrig />} />
      <Route path="/math/logarithm" element={<MathLogarithm />} />
      <Route path="/math/matrix" element={<MathMatrix />} />
      <Route path="/math/reference" element={<MathReference />} />

      {/* NerdMusic sub-suite (V52) — music theory reference */}
      <Route path="/music" element={<MusicHome />} />
      <Route path="/music/home" element={<MusicHome />} />
      <Route path="/music/reference" element={<MusicReference />} />
      {/* V62.1 — interactive music tools */}
      <Route path="/music/circle-of-fifths" element={<CircleOfFifths />} />
      <Route path="/music/scale-builder" element={<ScaleBuilder />} />

      {/* NerdHistory sub-suite (V55) — history reference */}
      <Route path="/history" element={<HistoryHome />} />
      <Route path="/history/home" element={<HistoryHome />} />
      <Route path="/history/reference" element={<HistoryReference />} />

      {/* NerdPhys sub-suite — projectile is the signature tool */}
      <Route path="/phys" element={<PhysHome />} />
      <Route path="/phys/home" element={<PhysHome />} />
      <Route path="/phys/projectile" element={<PhysProjectile />} />
      <Route path="/phys/kinematics" element={<PhysKinematics />} />
      <Route path="/phys/force" element={<PhysForce />} />
      <Route path="/phys/energy" element={<PhysEnergy />} />
      <Route path="/phys/ohms" element={<PhysOhms />} />
      <Route path="/phys/particles" element={<PhysParticles />} />
      <Route path="/phys/waves" element={<PhysWaves />} />
      <Route path="/phys/momentum" element={<PhysMomentum />} />
      <Route path="/phys/reference" element={<PhysReference />} />

      {/* NerdTools sub-suite — device utilities */}
      <Route path="/tools" element={<ToolsHome />} />
      <Route path="/tools/home" element={<ToolsHome />} />
      <Route path="/tools/converter" element={<ToolsConverter />} />
      {/* Level Pro REPLACES the prior Level route */}
      <Route path="/tools/level" element={<ToolsLevelPro />} />

      {/* V53 — NerdTools expansion: 36 new tools across 9 families */}
      {/* Family 1 — Level & Measure */}
      <Route path="/tools/picture-hanging" element={<ToolsPictureHanging />} />
      <Route path="/tools/object-height" element={<ToolsObjectHeight />} />
      <Route path="/tools/symmetry" element={<ToolsSymmetry />} />
      <Route path="/tools/photo-straightener" element={<ToolsPhotoStraightener />} />
      {/* Family 2 — Visual & Camera */}
      <Route path="/tools/magnifier" element={<ToolsMagnifier />} />
      <Route path="/tools/color-picker" element={<ToolsColorPicker />} />
      <Route path="/tools/brightness" element={<ToolsBrightness />} />
      <Route path="/tools/grid-camera" element={<ToolsGridCamera />} />
      <Route path="/tools/before-after" element={<ToolsBeforeAfter />} />
      {/* Family 3 — Sound & Audio */}
      <Route path="/tools/tone" element={<ToolsTone />} />
      <Route path="/tools/tuning-fork" element={<ToolsTuningFork />} />
      <Route path="/tools/metronome" element={<ToolsMetronome />} />
      <Route path="/tools/spectrogram" element={<ToolsSpectrogram />} />
      {/* Family 4 — Outdoor & Navigation */}
      <Route path="/tools/gps" element={<ToolsGPS />} />
      <Route path="/tools/altimeter" element={<ToolsAltimeter />} />
      <Route path="/tools/sun-moon" element={<ToolsSunMoon />} />
      <Route path="/tools/trail-marker" element={<ToolsTrailMarker />} />
      {/* Family 5 — DIY & Workshop */}
      <Route path="/tools/metal-detector" element={<ToolsMetalDetector />} />
      <Route path="/tools/square-checker" element={<ToolsSquareChecker />} />
      <Route path="/tools/drill-bit" element={<ToolsDrillBit />} />
      <Route path="/tools/roof-pitch" element={<ToolsRoofPitch />} />
      <Route path="/tools/coverage" element={<ToolsCoverage />} />
      {/* Family 6 — Math & Engineering */}
      <Route path="/tools/fraction" element={<ToolsFraction />} />
      <Route path="/tools/right-triangle" element={<ToolsRightTriangle />} />
      <Route path="/tools/resistor" element={<ToolsResistor />} />
      <Route path="/tools/torque" element={<ToolsTorque />} />
      {/* Family 7 — Time & Counting */}
      <Route path="/tools/interval" element={<ToolsInterval />} />
      <Route path="/tools/tally" element={<ToolsTally />} />
      <Route path="/tools/reaction" element={<ToolsReaction />} />
      {/* Family 8 — Sensor Lab */}
      <Route path="/tools/vibration" element={<ToolsVibration />} />
      <Route path="/tools/magnetic-field" element={<ToolsMagneticField />} />
      <Route path="/tools/steadiness" element={<ToolsSteadiness />} />
      <Route path="/tools/shake-counter" element={<ToolsShakeCounter />} />
      {/* Family 9 — Practical / Generators */}
      <Route path="/tools/currency" element={<ToolsCurrency />} />
      <Route path="/tools/password" element={<ToolsPassword />} />
      <Route path="/tools/exif" element={<ToolsExif />} />
      <Route path="/tools/compass" element={<ToolsCompass />} />
      <Route path="/tools/decibel" element={<ToolsDecibel />} />
      <Route path="/tools/ruler" element={<ToolsRuler />} />
      <Route path="/tools/qr" element={<ToolsQR />} />

      {/* NerdBio sub-suite — genetics/molecular + anatomy reference */}
      <Route path="/bio" element={<BioHome />} />
      <Route path="/bio/home" element={<BioHome />} />
      <Route path="/bio/dna" element={<BioDNA />} />
      <Route path="/bio/translate" element={<BioTranslate />} />
      <Route path="/bio/punnett" element={<BioPunnett />} />
      <Route path="/bio/hardy-weinberg" element={<BioHardyWeinberg />} />
      <Route path="/bio/lab" element={<BioLab />} />
      <Route path="/bio/anatomy" element={<BioAnatomy />} />
      <Route path="/bio/reference" element={<BioReference />} />

      {/* Tool suggestion form */}
      <Route path="/suggest" element={<Suggest />} />

      {/* 404 catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </>
  );
}
